import axios from 'axios';

// Use environment variable for base URL
const getBaseURL = () => {
    const baseUrl = import.meta.env.VITE_EMPLOAPI_BASE_URL;
    
    if (!baseUrl) {
        console.error('VITE_EMPLOAPI_BASE_URL environment variable is not set!');
        console.error('This will cause API requests to fail in production.');
        console.error('Please set VITE_EMPLOAPI_BASE_URL to your backend API URL (e.g., https://emploapi.azurewebsites.net)');
        
        // For development, use the proxy path
        if (import.meta.env.DEV) {
            console.warn('Using fallback proxy path for development: /emploapi');
            return '/emploapi';
        }
        
        // For production, this will cause issues
        console.error('No fallback available for production. API requests will fail.');
        return undefined;
    }
    
    console.log('EmploAPI Base URL:', baseUrl);
    return baseUrl;
};

const emploApiClient = axios.create({
    baseURL: getBaseURL(),
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000, // 10 second timeout
});



// Add request interceptor for auth token (user authentication only)
emploApiClient.interceptors.request.use((config) => {
    // Only add auth for non-email endpoints (email endpoints are public)
    const userToken = localStorage.getItem('accessToken');
    if (userToken && !config.url?.includes('/api/email')) {
        config.headers.Authorization = `Bearer ${userToken}`;
    }
    
    // Log the request for debugging
    console.log('EmploAPI Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        hasAuth: !!config.headers.Authorization
    });
    
    return config;
});

// Add response interceptor for error handling
emploApiClient.interceptors.response.use(
    (response) => {
        // Log successful responses
        console.log('EmploAPI Success:', {
            status: response.status,
            url: response.config.url,
            dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
            dataLength: Array.isArray(response.data) ? response.data.length : 'N/A'
        });
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    const response = await emploApiClient.post('/auth/refresh', { refreshToken });
                    const { accessToken } = response.data;
                    localStorage.setItem('accessToken', accessToken);
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return emploApiClient(originalRequest);
                }
            } catch (error) {
                // Refresh token failed, clear tokens
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                console.error('Token refresh failed:', error);
            }
        }
        
        // Enhanced error logging for debugging
        console.error('EmploAPI Error:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            config: {
                method: error.config?.method,
                url: error.config?.url,
                baseURL: error.config?.baseURL,
                fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'N/A'
            },
            isNetworkError: error.code === 'ERR_NETWORK',
            isCorsError: error.message.includes('CORS') || error.message.includes('Network Error')
        });
        
        return Promise.reject(error);
    }
);

export default emploApiClient; 